import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  MDBBtn,
  MDBContainer,
  MDBModal,
  MDBModalBody,
  MDBModalContent,
  MDBModalDialog,
  MDBModalFooter,
  MDBModalHeader,
  MDBModalTitle,
  MDBSpinner,
} from "mdb-react-ui-kit";
import "./BatchesPage.css";
import config from "../config";
import { getBatchSkus } from "../services/batchesApi";

const API_BASE_URL = `${config.API_BASE_URL.replace(/\/$/, "")}/api/`;

function formatDateTime(value) {
  try {
    const d = new Date(value);
    // Example: 2026-05-02 12:34:00
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    const hh = String(d.getHours()).padStart(2, "0");
    const mi = String(d.getMinutes()).padStart(2, "0");
    const ss = String(d.getSeconds()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd} ${hh}:${mi}:${ss}`;
  } catch {
    return String(value || "");
  }
}

function PrintIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M7 8V3h10v5M7 17H5a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M7 14h10v7H7v-7Z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function typeLabel(type) {
  const t = String(type || "").toLowerCase();
  if (t.includes("smartscan")) return "Smartscan";
  if (t.includes("smartlist")) return "Smartlist";
  return type || "—";
}

function dotClass(type) {
  const t = String(type || "").toLowerCase();
  if (t.includes("smartscan")) return "smartscan";
  if (t.includes("smartlist")) return "smartlist";
  return "";
}

export default function BatchesPage() {
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("");

  const companyId = localStorage.getItem("company_id");

  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(false);

  // Preview modal state
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState("");
  const [previewData, setPreviewData] = useState(null);
  const [activeBatchId, setActiveBatchId] = useState(null);

  const fetchBatches = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch(
        `${API_BASE_URL}batches?company_id=${encodeURIComponent(companyId || "")}`,
      );
      const data = await res.json();
      if (data.success) {
        setBatches(data.data || []);
      } else {
        setBatches([]);
      }
    } catch (err) {
      console.error("Batch API Error:", err);
      setBatches([]);
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    fetchBatches();
  }, [fetchBatches]);

  // FILTERING
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();

    return batches.filter((b) => {
      const matchesSearch =
        !q ||
        String(b.batchId).includes(q) ||
        String(b.createdAt).toLowerCase().includes(q) ||
        typeLabel(b.type).toLowerCase().includes(q);

      const matchesType =
        !typeFilter ||
        String(b.type).toLowerCase() === typeFilter;

      return matchesSearch && matchesType;
    });
  }, [batches, search, typeFilter]);

  // Preview popup (loads SKUs so user can see before print)
  const openPreview = async (batch) => {
    const id = batch?.batchId;
    if (!id) return;
    setActiveBatchId(id);
    setPreviewOpen(true);
    setPreviewLoading(true);
    setPreviewError("");
    setPreviewData(null);
    try {
      const data = await getBatchSkus(id);
      setPreviewData(data);
    } catch (e) {
      const msg =
        e?.response?.data?.message ||
        e?.message ||
        "Failed to load SKUs from the server.";
      setPreviewError(msg);
    } finally {
      setPreviewLoading(false);
    }
  };

  const closePreview = () => {
    setPreviewOpen(false);
    setPreviewLoading(false);
    setPreviewError("");
    setPreviewData(null);
    setActiveBatchId(null);
  };

  // Open new tab print page (clean page)
  const openPrintTab = () => {
    if (!activeBatchId) return;
    const url = `/batches/print/${encodeURIComponent(String(activeBatchId))}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };


  return (
    <div className="batches-page">
      <MDBContainer fluid className="py-4 px-3 px-md-4">
        <div className="mx-auto" style={{ maxWidth: 1200 }}>
          <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
            <h3 className="fw-bold mb-0">Batches</h3>
            <div className="d-flex gap-2">
              <MDBBtn color="light" size="sm" type="button" onClick={() => { setSearch(""); setTypeFilter(""); }}>
                Reset
              </MDBBtn>
              <MDBBtn
                color="danger"
                size="sm"
                type="button"
                disabled={loading}
                onClick={() => fetchBatches()}
              >
                Refresh
              </MDBBtn>
            </div>
          </div>

          <div className="bg-white rounded-4 border shadow-sm p-3 p-md-4">
            <div className="batches-toolbar mb-3">
              <input
                className="form-control search"
                placeholder="Search..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />

              <select
                className="form-select"
                style={{ width: 220 }}
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
              >
                <option value="">Filter by Type</option>
                <option value="smartscan">Smartscan</option>
                <option value="smartlist">Smartlist</option>
              </select>
            </div>

            <div style={{ overflowX: "auto" }}>
              <table className="batches-table">
                <thead>
                  <tr>
                    <th style={{ width: 120 }}>Batch ID</th>
                    <th style={{ width: 220 }}>Created at</th>
                    <th style={{ width: 180 }}>Type</th>
                    <th style={{ width: 100, textAlign: "right" }}>Print</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={4} style={{ padding: 18, color: "#6b7280" }}>
                        Loading batches...
                      </td>
                    </tr>
                  ) : filtered.length === 0 ? (
                    <tr>
                      <td colSpan={4} style={{ padding: 18, color: "#6b7280" }}>
                        No batches found.
                      </td>
                    </tr>
                  ) : (
                    filtered.map((b) => (
                      <tr key={b.batchId}>
                        <td style={{ fontWeight: 700, color: "#111827" }}>{b.batchId}</td>
                        <td>{formatDateTime(b.createdAt)}</td>
                        <td>
                          <span className="type-pill">
                            <span className={`type-dot ${dotClass(b.type)}`} />
                            {typeLabel(b.type)}
                          </span>
                        </td>
                        <td style={{ textAlign: "right" }}>
                          <button
                            type="button"
                            className="print-btn"
                            title="Print"
                            onClick={() => openPreview(b)}
                          >
                            <PrintIcon />
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </MDBContainer>

      <MDBModal open={previewOpen} tabIndex="-1" staticBackdrop>
        <MDBModalDialog size="lg" centered scrollable>
          <MDBModalContent>
            <MDBModalHeader>
              <MDBModalTitle>Batch Print Preview</MDBModalTitle>
              <button
                type="button"
                className="btn-close"
                aria-label="Close"
                onClick={closePreview}
              />
            </MDBModalHeader>
            <MDBModalBody>
              {previewLoading ? (
                <div className="py-4 d-flex align-items-center justify-content-center gap-2">
                  <MDBSpinner size="sm" />
                  <span className="text-muted">Loading batch SKUs...</span>
                </div>
              ) : previewError ? (
                <div className="text-danger">{previewError}</div>
              ) : previewData ? (
                <>
                  <div className="batch-preview-header">
                    <div className="batch-preview-company">{previewData.companyName}</div>
                    <div className="batch-preview-batch">Batch #{previewData.batchId}</div>
                  </div>
                  <div className="batch-preview-grid">
                    {previewData.items.map((it) => {
                      const sku = it.sku;
                      const qrSrc = `https://api.qrserver.com/v1/create-qr-code/?size=210x210&data=${encodeURIComponent(
                        sku,
                      )}`;
                      return (
                        <div key={sku} className="batch-preview-card">
                          <img className="batch-preview-qr" src={qrSrc} alt={sku} />
                          <p className="batch-preview-sku">{sku}</p>
                        </div>
                      );
                    })}
                  </div>
                </>
              ) : null}
            </MDBModalBody>
            <MDBModalFooter>
              <MDBBtn color="light" type="button" onClick={closePreview}>
                Close
              </MDBBtn>
              <MDBBtn
                color="danger"
                type="button"
                onClick={openPrintTab}
                disabled={previewLoading || !previewData}
              >
                Print
              </MDBBtn>
            </MDBModalFooter>
          </MDBModalContent>
        </MDBModalDialog>
      </MDBModal>
    </div>
  );
}

